import React, { useState, useMemo, useCallback } from 'react';
import Modal from 'pages/components/Modal/Modal';
import CustomButton from 'pages/components/CustomButton/CustomButton';
import QuoteRequestForm from './QuoteRequest/QuoteRequestForm';
import Certifications from './Certifications/Certifications';
import BVLOSOperations from './BVLOSOperations/BVLOSOperations';
import DroneSpecifications from './DroneSpecification/DroneSpecification';
import DownloadComponent from './pages/components/DownloadComponent';
import HeroSection from './pages/components/HeroSection';
import { useApi } from 'hooks/useApi';
import styles from './PeregrineDrone.module.css';

const PeregrineDrone = () => {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const staticFeatures = useMemo(() => [
    { icon: '../../Assets/Images/peregrine/realtime.svg', description: 'Real-time broadcasting' },
    { icon: '../../Assets/Images/peregrine/thermal.svg', description: 'Thermal Camera' },
    { icon: '../../Assets/Images/peregrine/nightvision.svg', description: 'Day / night vision' },
    { icon: '../../Assets/Images/peregrine/carbonfiber.svg', description: 'Carbon Fiber, ABS & Aluminum Built' },
    { icon: '../../Assets/Images/peregrine/compact_easy.svg', description: 'Compact and Easy to Handle' },
    { icon: '../../Assets/Images/peregrine/exceptional_project.svg', description: 'Exceptional Project Management' },
    { icon: '../../Assets/Images/peregrine/dynamic_flight.svg', description: 'Dynamic Flight Diversion Capability' }
  ], []);

  const { data: productData, loading: productLoading, error: productError } = useApi('/product-types', { params: { slug: 'peregrine' } });
  const { data: specificationsResponse, loading: specsLoading, error: specsError } = useApi('/products', { params: { slug: 'peregrine' } });

  const specificationsData = useMemo(() => {
    if (!specificationsResponse?.success || !specificationsResponse?.products) return [];
    return specificationsResponse.products.map(product => ({
      ...product,
      specifications: product.specification?.map(spec => ({ label: spec.name, value: spec.value })) || []
    }));
  }, [specificationsResponse]);

  const getOperationsData = useMemo(() => {
    if (!productData?.success || !productData?.products?.designed_for) return [];
    return productData.products.designed_for.map(item => ({
      icon: item.icon,
      label: item.title,
      image: item.image || 'Assets/Images/peregrine/BVLOS.svg'
    }));
  }, [productData]);

  const getFeaturesData = useMemo(() => {
    if (!productData?.success || !productData?.products?.features) return [];
    return productData.products.features.map(feature => ({
      icon: feature.icon,
      description: feature.short_description || feature.title
    }));
  }, [productData]);

  const product = useMemo(() => productData?.success ? productData.products : null, [productData]);

  const handleOpenQuoteModal = useCallback(() => setIsQuoteModalOpen(true), []);
  const handleCloseQuoteModal = useCallback(() => setIsQuoteModalOpen(false), []);

  const loading = productLoading || specsLoading;
  const error = productError || specsError;

  if (loading) return (
    <div className={styles.pageWrapper}>
      <div className={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className={styles.pageWrapper}>
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
      </div>
    </div>
  );

  return (
    <div className={styles.pageWrapper}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <HeroSection
          subtitle={product?.title || "Peregrine"}
          title={product?.banner_heading || "Sky's Most Advanced Performer."}
          gradientFrom="#F97738"
          gradientTo="#FCB999"
          showBottomFade={false}
        />
      </section>

      {/* Drone Overlap Section */}
      <div className={styles.droneWrapper}>
        <img
          id="droneImage"
          src={product?.banner_image ? `${process.env.REACT_APP_CMS_BASE_URL}/attachments/${product.banner_image}` : '../../Assets/Images/peregrine/PeregrineDrone.svg'}
          alt="Peregrine Drone"
          className={styles.droneImage}
          width="800"
          height="600"
          loading="eager"
        />
      </div>

      {/* Main Content */}
      <div className={styles.mainContainer}>

        {/* Description */}
        <section className={styles.descriptionSection}>
          <h2>
            {product?.description || "Elite Carbon Fiber Quadcopter Airframe – Precision Crafted for Enduring Performance and Impeccable Flight Stability"}
          </h2>
        </section>

        {/* Features */}
        <section className={styles.featuresSection}>
          <div className={styles.featuresGrid}>
            {(getFeaturesData.length > 0 ? getFeaturesData : staticFeatures).map((feature, index) => (
              <div key={index} className={styles.featureItem}>
                <img
                  src={feature.icon ? `${process.env.REACT_APP_CMS_BASE_URL}/attachments/${feature.icon}` : feature.icon}
                  alt={feature.description}
                  width="36"
                  height="36"
                  loading="lazy"
                />
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Additional Images */}
        {(product?.image1 || product?.image2 || product?.image3) && (
          <section className={styles.additionalImagesSection}>
            <div className={styles.imagesGrid}>
              {product?.image1 && (
                <img
                  src={`${process.env.REACT_APP_CMS_BASE_URL}/attachments/${product.image1}`}
                  alt="Additional Image 1"
                  width="400"
                  height="300"
                  loading="lazy"
                />
              )}
              {product?.image2 && (
                <img
                  src={`${process.env.REACT_APP_CMS_BASE_URL}/attachments/${product.image2}`}
                  alt="Additional Image 2"
                  width="400"
                  height="300"
                  loading="lazy"
                />
              )}
              {product?.image3 && (
                <img
                  src={`${process.env.REACT_APP_CMS_BASE_URL}/attachments/${product.image3}`}
                  alt="Additional Image 3"
                  width="400"
                  height="300"
                  loading="lazy"
                />
              )}
            </div>
          </section>
        )}

        {/* Quote Button */}
        <section className={styles.quoteSection}>
          <CustomButton
            onClick={handleOpenQuoteModal}
            size="large"
            ariaLabel="Request a Quote"
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>}
          >
            Request a Quote
          </CustomButton>
        </section>

        {/* Quote Modal */}
        <Modal isOpen={isQuoteModalOpen} onClose={handleCloseQuoteModal} title="Request a Quote" maxWidth="40rem">
          <QuoteRequestForm onSuccess={handleCloseQuoteModal} productSlug={product?.slug} />
        </Modal>

        {/* Full Width Sections */}
        <Certifications description={false} />

        <BVLOSOperations
          title={product?.designed_for_title || "Designed For BVLOS Operations"}
          operations={getOperationsData}
        />

        {/* Specifications and Downloads */}
        <div className={styles.specificationWrapper}>
          {specificationsData.map((specProduct, index) => (
            <React.Fragment key={specProduct.slug || index}>
              <DroneSpecifications
                droneImage={specProduct.image ? `${process.env.REACT_APP_CMS_BASE_URL}/attachments/${specProduct.image}` : 'Assets/Images/peregrine/Drone_img.svg'}
                specifications={specProduct.specifications || []}
                title={specProduct.title}
                description={specProduct.description}
              />

              <DownloadComponent
                brochure={specProduct.brochure ? `${process.env.REACT_APP_CMS_BASE_URL}/attachments/${specProduct.brochure}` : null}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PeregrineDrone;
